import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Divider
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Radio,
  CheckCircle,
  Cancel,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { radioInfoService } from '../../services/radioInfoService';

const RadioInfoManagement = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRadioInfo, setEditingRadioInfo] = useState(null);
  const [formData, setFormData] = useState({
    protocol: 'icecast',
    server_host: '',
    server_port: 8000,
    bitrate: 128,
    password: '',
    player_url: '',
    is_active: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();

  // Buscar informações da rádio
  const { data: radioInfoData, isLoading } = useQuery({
    queryKey: ['radioInfo'],
    queryFn: () => radioInfoService.getAllRadioInfo(),
  });

  const radioInfoList = radioInfoData?.data?.radioInfo || [];

  // Mutations
  const createRadioInfoMutation = useMutation({
    mutationFn: (data) => radioInfoService.createRadioInfo(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['radioInfo']);
      setSnackbar({ open: true, message: 'Informações da rádio criadas com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao criar informações da rádio', severity: 'error' });
    }
  });

  const updateRadioInfoMutation = useMutation({
    mutationFn: ({ id, data }) => radioInfoService.updateRadioInfo(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['radioInfo']);
      setSnackbar({ open: true, message: 'Informações da rádio atualizadas com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao atualizar informações da rádio', severity: 'error' });
    }
  });

  const deleteRadioInfoMutation = useMutation({
    mutationFn: (id) => radioInfoService.deleteRadioInfo(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['radioInfo']);
      setSnackbar({ open: true, message: 'Informações da rádio deletadas com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao deletar informações da rádio', severity: 'error' });
    }
  });

  const setActiveRadioInfoMutation = useMutation({
    mutationFn: (id) => radioInfoService.setActiveRadioInfo(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['radioInfo']);
      setSnackbar({ open: true, message: 'Informações da rádio ativadas com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao ativar informações da rádio', severity: 'error' });
    }
  });

  const handleOpenDialog = (radioInfo = null) => {
    if (radioInfo) {
      setEditingRadioInfo(radioInfo);
      setFormData({
        protocol: radioInfo.protocol || 'icecast',
        server_host: radioInfo.server_host || '',
        server_port: radioInfo.server_port || 8000,
        bitrate: radioInfo.bitrate || 128,
        password: radioInfo.password || '',
        player_url: radioInfo.player_url || '',
        is_active: radioInfo.is_active !== undefined ? radioInfo.is_active : true
      });
    } else {
      setEditingRadioInfo(null);
      setFormData({
        protocol: 'icecast',
        server_host: '',
        server_port: 8000,
        bitrate: 128,
        password: '',
        player_url: '',
        is_active: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRadioInfo(null);
    setFormData({
      protocol: 'icecast',
      server_host: '',
      server_port: 8000,
      bitrate: 128,
      password: '',
      player_url: '',
      is_active: true
    });
    setShowPassword(false);
  };

  const handleSubmit = () => {
    // Ensure numeric fields are properly converted
    const submitData = {
      ...formData,
      server_port: parseInt(formData.server_port) || 8000,
      bitrate: parseInt(formData.bitrate) || 128
    };
    
    if (editingRadioInfo) {
      updateRadioInfoMutation.mutate({ id: editingRadioInfo.id, data: submitData });
    } else {
      createRadioInfoMutation.mutate(submitData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja deletar estas informações da rádio?')) {
      deleteRadioInfoMutation.mutate(id);
    }
  };

  const handleSetActive = (id) => {
    setActiveRadioInfoMutation.mutate(id);
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'default';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  const getProtocolLabel = (protocol) => {
    const protocols = {
      icecast: 'Icecast',
      shoutcast: 'Shoutcast',
      other: 'Outro'
    };
    return protocols[protocol] || protocol;
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              <Radio sx={{ mr: 1, verticalAlign: 'middle' }} />
              Informações da Rádio
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#FF8A65' }
              }}
            >
              Adicionar Configuração
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Instruções:</strong> Configure as informações do servidor de streaming para que os DJs possam conectar seus softwares de transmissão ao vivo.
            </Typography>
          </Alert>

          <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Protocolo</TableCell>
                  <TableCell>Servidor</TableCell>
                  <TableCell>Porta</TableCell>
                  <TableCell>Taxa de Bits</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {radioInfoList.map((radioInfo) => (
                  <TableRow key={radioInfo.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {getProtocolLabel(radioInfo.protocol)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {radioInfo.server_host}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {radioInfo.server_port}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {radioInfo.bitrate} kbps
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(radioInfo.is_active)}
                        color={getStatusColor(radioInfo.is_active)}
                        size="small"
                        icon={radioInfo.is_active ? <CheckCircle /> : <Cancel />}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(radioInfo)}
                            sx={{ color: '#00D4FF' }}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        
                        {!radioInfo.is_active && (
                          <Tooltip title="Ativar">
                            <IconButton
                              size="small"
                              onClick={() => handleSetActive(radioInfo.id)}
                              sx={{ color: '#4CAF50' }}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(radioInfo.id)}
                            sx={{ color: '#FF1744' }}
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {radioInfoList.length === 0 && (
            <Box textAlign="center" py={4}>
              <Radio sx={{ fontSize: 60, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
              <Typography variant="h6" sx={{ opacity: 0.7 }}>
                Nenhuma configuração encontrada
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Adicione as informações do servidor de streaming
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialog para Editar/Criar Informações da Rádio */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingRadioInfo ? 'Editar Informações da Rádio' : 'Adicionar Informações da Rádio'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Protocolo</InputLabel>
              <Select
                value={formData.protocol}
                onChange={(e) => setFormData({ ...formData, protocol: e.target.value })}
                label="Protocolo"
              >
                <MenuItem value="icecast">Icecast</MenuItem>
                <MenuItem value="shoutcast">Shoutcast</MenuItem>
                <MenuItem value="other">Outro</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Nome do Host do Servidor"
              value={formData.server_host}
              onChange={(e) => setFormData({ ...formData, server_host: e.target.value })}
              margin="normal"
              placeholder="exemplo: stream.exemplo.com"
              helperText="Endereço do servidor de streaming"
            />

            <TextField
              fullWidth
              label="Porta do Servidor"
              type="number"
              value={formData.server_port}
              onChange={(e) => setFormData({ ...formData, server_port: parseInt(e.target.value) || 8000 })}
              margin="normal"
              inputProps={{ min: 1, max: 65535 }}
              helperText="Porta do servidor (geralmente 8000 para Icecast)"
            />

            <TextField
              fullWidth
              label="Taxa de Bits"
              type="number"
              value={formData.bitrate}
              onChange={(e) => setFormData({ ...formData, bitrate: parseInt(e.target.value) || 128 })}
              margin="normal"
              inputProps={{ min: 32, max: 320 }}
              helperText="Taxa de bits em kbps (recomendado: 128)"
            />

            <TextField
              fullWidth
              label="Senha"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              margin="normal"
              helperText="Senha para conectar ao servidor de streaming"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                )
              }}
            />

            <TextField
              fullWidth
              label="Player para seu Blog"
              value={formData.player_url}
              onChange={(e) => setFormData({ ...formData, player_url: e.target.value })}
              margin="normal"
              placeholder="https://exemplo.com/player"
              helperText="URL do player para incorporar em blogs/sites (opcional)"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Configuração Ativa"
              sx={{ mt: 2 }}
            />

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Atenção:</strong> Ao ativar uma nova configuração, a configuração anterior será desativada automaticamente.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createRadioInfoMutation.isPending || updateRadioInfoMutation.isPending}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {(createRadioInfoMutation.isPending || updateRadioInfoMutation.isPending) ? 
              <CircularProgress size={20} /> : 'Salvar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RadioInfoManagement;
